// from https://github.com/rednez/angular-user-idle

import { Inject, Injectable, InjectionToken, NgZone, Optional } from '@angular/core';
import { from, fromEvent, interval, merge, Observable, Subject, Subscription } from 'rxjs';
import { bufferTime, filter, map, scan, takeUntil, tap } from 'rxjs/operators';

export const USER_IDLE_CONFIG = new InjectionToken<UserIdleConfig>('USER_IDLE_CONFIG');

export class UserIdleConfig {
  /**
   * Idle value in seconds.
   */
  idle?: number;

  /**
   * The number of seconds before a timeout to issue a warning
   */
  timeoutWarningSeconds?: number;

  /**
   * Timeout value in seconds.
   */
  timeout?: number;
  /**
   * Ping value in seconds.
   */
  ping?: number;
  /**
   * IdleSensitivity time that activity must remain below the idle detection threshold before
   * idle buffer timer count user's activity actions, in seconds.
   */
  idleSensitivity?: number;
}

@Injectable({ providedIn: 'root' })
export class UserIdleService {
  ping$: Observable<any>;

  /**
   * Events that can interrupts user's inactivity timer.
   */
  protected activityEvents$: Observable<any>;

  protected timerStart$ = new Subject<boolean>();
  protected idleDetected$ = new Subject<boolean>();
  protected timeoutWarning$ = new Subject<boolean>();
  protected timeout$ = new Subject<boolean>();
  protected idle$: Observable<any>;

  /**
   * Idle value in milliseconds.
   * Default equals to 10 minutes.
   */
  protected idleMillisec = 60 * 1000;

  /**
   * Idle buffer wait time milliseconds to collect user action
   * Default equals to 1 Sec.
   */
  protected idleSensitivityMillisec = 1000;

  /**
   * The number of seconds before a timeout to issue a warning
   */
  protected timeoutWarningSeconds = 2 * 60;

  /**
   * Timeout value in seconds.
   * Default equals to 5 minutes.
   */
  protected timeout = 5 * 60;

  /**
   * Ping value in milliseconds.
   * Default equals to 2 minutes.
   */
  protected pingMillisec = 120 * 1000;

  /**
   * Prevents duplicate timeout warnings.
   */
  protected hasSentTimeoutWarning: boolean;

  /**
   * Timeout status.
   */
  protected isTimeout: boolean;

  protected isIdleDetected: boolean;

  protected idleSubscription: Subscription;

  constructor(@Optional() @Inject(USER_IDLE_CONFIG) config: UserIdleConfig, private _ngZone: NgZone) {
    if (config) {
      this.setConfig(config);
    }
  }

  static nowInSeconds(): number {
    return Math.floor(Date.now() / 1000);
  }

  /**
   * Start watching for user idle and setup timer and ping.
   */
  startWatching() {
    if (!this.activityEvents$) {
      this.activityEvents$ = merge(
        fromEvent(window, 'mousemove'),
        fromEvent(window, 'mousedown'),
        fromEvent(window, 'resize'),
        fromEvent(document, 'keydown')
      );
    }

    this.idle$ = from(this.activityEvents$);

    // Start last activity as now
    localStorage.setItem('lastActivityTimestamp', JSON.stringify(UserIdleService.nowInSeconds()));

    this.activityEvents$
      .pipe(
        bufferTime(this.idleSensitivityMillisec),
        filter(arr => arr.length > 0),
        tap(() => {
          localStorage.setItem('lastActivityTimestamp', JSON.stringify(UserIdleService.nowInSeconds()));
          this.isIdleDetected = false;
          this.isTimeout = false;
          this.hasSentTimeoutWarning = false;
          this.idleDetected$.next(false);
        })
      )
      .subscribe();

    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }

    this.idleSubscription = this.idle$
      .pipe(
        bufferTime(this.idleSensitivityMillisec), // Starting point of detecting of user's inactivity
        filter(arr => !arr.length && !this.isIdleDetected),
        tap(() => {
          this.isIdleDetected = true;
          this.idleDetected$.next(true);

          this.setupTimer();
        })
      )
      .subscribe();

    this.setupPing(this.pingMillisec);
  }

  stopWatching() {
    this.hasSentTimeoutWarning = false;
    this.isTimeout = false;
    this.isIdleDetected = false;
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }
  }

  /**
   * Return observable for idle status changed
   */
  onIdleStatusChanged(): Observable<boolean> {
    return this.idleDetected$.asObservable();
  }

  /**
   * Return observable for timeout is fired.
   */
  onTimeoutWarning(): Observable<boolean> {
    return this.timeoutWarning$.pipe(
      filter(timeoutWarning => !!timeoutWarning),
      tap(() => (this.hasSentTimeoutWarning = true)),
      map(() => true)
    );
  }

  /**
   * Return observable for timeout is fired.
   */
  onTimeout(): Observable<boolean> {
    return this.timeout$.pipe(
      filter(timeout => !!timeout),
      tap(() => (this.isTimeout = true)),
      map(() => true)
    );
  }

  getConfigValue(): UserIdleConfig {
    return {
      idle: this.idleMillisec,
      idleSensitivity: this.idleSensitivityMillisec,
      timeout: this.timeout,
      ping: this.pingMillisec
    };
  }

  /**
   * Set config values.
   *
   * @param config
   */
  setConfigValues(config: UserIdleConfig) {
    if (this.idleSubscription && !this.idleSubscription.closed) {
      console.error('Call stopWatching() before set config values');
      return;
    }

    this.setConfig(config);
  }

  /**
   * Set custom activity events
   *
   * @param customEvents Example: merge(
   *   fromEvent(window, 'mousemove'),
   *   fromEvent(window, 'resize'),
   *   fromEvent(document, 'keydown'),
   *   fromEvent(document, 'touchstart'),
   *   fromEvent(document, 'touchend')
   * )
   */
  setCustomActivityEvents(customEvents: Observable<any>) {
    if (this.idleSubscription && !this.idleSubscription.closed) {
      console.error('Call stopWatching() before set custom activity events');
      return;
    }

    this.activityEvents$ = customEvents;
  }

  /**
   * Setup timer.
   *
   * Counts every seconds and return n+1 and fire timeout either for last count or if last activity was too long ago.
   */
  protected setupTimer() {
    this._ngZone.runOutsideAngular(() => {
      interval(1000)
        .pipe(takeUntil(merge(this.activityEvents$, this.timeout$)))
        .subscribe(() => {
          const lastActivityTimestamp = JSON.parse(localStorage.getItem('lastActivityTimestamp')) as number;

          if (!lastActivityTimestamp || lastActivityTimestamp + this.timeout < UserIdleService.nowInSeconds()) {
            this.timeout$.next(true);
          } else if (
            !this.hasSentTimeoutWarning &&
            lastActivityTimestamp + (this.timeout - this.timeoutWarningSeconds) < UserIdleService.nowInSeconds()
          ) {
            this.timeoutWarning$.next(true);
          }
        });
    });
  }

  /**
   * Setup ping.
   *
   * Pings every ping-seconds only if is not timeout.
   *
   * @param pingMillisec
   */
  protected setupPing(pingMillisec: number) {
    this.ping$ = interval(pingMillisec).pipe(filter(() => !this.isTimeout));
  }

  private setConfig(config: UserIdleConfig) {
    if (config.idle) {
      this.idleMillisec = config.idle * 1000;
    }
    if (config.ping) {
      this.pingMillisec = config.ping * 1000;
    }
    if (config.idleSensitivity) {
      this.idleSensitivityMillisec = config.idleSensitivity * 1000;
    }
    if (config.timeout) {
      this.timeout = config.timeout;
    }
    if (config.timeoutWarningSeconds) {
      this.timeoutWarningSeconds = config.timeoutWarningSeconds;
    }
  }
}
